import React, { FC, createContext, useState, useEffect, useContext, useRef } from 'react';
import { TrackingInfo } from '../types';
import { useConsent } from './consent-provider';

export interface AnalyticsContextType {
    hasConsent: boolean;
    gTagLoaded: boolean;
}

export const AnalyticsContext = createContext<AnalyticsContextType>( {
    hasConsent: false,
    gTagLoaded: false
} );

type GTagManagerScripts = Record<'lib' | 'init', HTMLScriptElement | null>;

export const useCreateAnalytics = ( tracking?: TrackingInfo ): AnalyticsContextType => {
    const [ hasConsent, setHasConsent ] = useState<boolean>( true );
    const [ gTagLoaded, setGTagLoaded ] = useState<boolean>(false);
    const {} = useConsent();

    const scriptsRef = useRef<GTagManagerScripts>( {
        init: null,
        lib: null
    } );

    const removeLibs = () => {
        const { lib, init } = scriptsRef.current;
        setGTagLoaded(false);
        if ( lib ) {
            document.body.removeChild( lib );
        }
        if ( init ) {
            document.body.removeChild( init );
        }
    }

    const handleGoogleAnalyticsReady = () => {
        setGTagLoaded(true);

        gtag('event', 'foo', {
            snoot: 'moot'
        })
    }

    useEffect( () => {
        if ( window && document ) {
            if ( tracking && hasConsent ) {
                const { lib, init } = scriptsRef.current;
                if ( (lib && lib.id !== tracking.gaCode) || (init && init.id !== tracking.gaCode) ) {
                    removeLibs();
                }

                const props = {
                    debug_mode: process.env.NODE_ENV !== 'production'
                }

                const libElement = document.createElement( 'script' );
                libElement.id = tracking.gaCode;
                libElement.async = true;
                libElement.src = `https://www.googletagmanager.com/gtag/js?id=${ tracking.gaCode }`;

                const initElement = document.createElement( 'script' );
                initElement.id = tracking.gaCode;
                initElement.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${tracking.gaCode}', ${JSON.stringify(props)});`;

                document.body.appendChild( libElement );
                document.body.appendChild( initElement );
                scriptsRef.current = {
                    lib: libElement,
                    init: initElement
                }

                gtag('event', 'page_view', {
                    'event_callback': handleGoogleAnalyticsReady
                });
            } else {
                removeLibs();
            }
        }
    }, [ tracking, hasConsent ] );

    return {
        hasConsent,
        gTagLoaded
    };
}

export const useAnalytics = (): AnalyticsContextType => {
    return useContext( AnalyticsContext );
}

export interface AnalyticsProviderProps {
    tracking?: TrackingInfo;
}

export const AnalyticsProvider: FC<AnalyticsProviderProps> = ( { tracking, children } ) => {
    const contextValue = useCreateAnalytics( tracking );

    return (
        <AnalyticsContext.Provider value={ contextValue }>
            { children }
        </AnalyticsContext.Provider>
    )
}